import { GuestDetail, Item } from 'shared-types';
import { isBasketItemExpired } from '@store/basket/utils/isBasketItemExpired';
import { hasExtras, RoomExtrasOfferedParams } from './roomExtrasOffered';

type RoomIds = { roomId?: string; rateId?: string; promoRateCode?: string | null };

const roomIds = (item: Item): RoomIds => (item.bookable?.ids ?? {}) as RoomIds;

const childAgesOf = (item: Item): number[] => {
  const ages = (item.bookable as { roomsChildAges?: unknown } | undefined)?.roomsChildAges;
  return Array.isArray(ages) ? ages : [];
};

const hasPackageChildren = (item: Item, items: Item[]) =>
  items.some(
    ({ packageBasketItemId }) =>
      packageBasketItemId != null && String(packageBasketItemId) === String(item.id)
  );

export const isUpgradeableRoomItem = (item: Item, items: Item[]) => {
  const { roomId, rateId } = roomIds(item);
  return (
    item.type === 'rooms' &&
    item.id != null &&
    !!roomId &&
    !!rateId &&
    !!item.checkIn &&
    !!item.checkOut &&
    item.packageBasketItemId == null &&
    item.dynamicPackage !== 'child' &&
    !hasPackageChildren(item, items) &&
    !item.spaPackage &&
    !hasExtras(item.bookable?.roomExtras) &&
    item.bookable?.status !== 'failed' &&
    !isBasketItemExpired(item)
  );
};

export const toRoomExtrasParams = (item: Item): RoomExtrasOfferedParams => {
  const { roomId, rateId } = roomIds(item);
  return {
    roomId: roomId as string,
    rateId: rateId as string,
    adults: item.adults ?? 0,
    children: item.children ?? 0,
    fromDate: item.checkIn as string,
    toDate: item.checkOut as string,
  };
};

export const toUpgradeAvailabilityParams = (item: Item) => {
  const { roomId, rateId, promoRateCode } = roomIds(item);
  const adults = item.adults ?? 0;
  const children = item.children ?? 0;
  return {
    id: roomId as string,
    rateId: rateId as string,
    fromDate: item.checkIn as string,
    toDate: item.checkOut as string,
    adults,
    children,
    childAges: Object.fromEntries(childAgesOf(item).map((age, index) => [index, age])),
    rooms: [{ roomNumber: 1, adults, children }],
    ...(promoRateCode ? { promoRateCode } : {}),
  };
};

export const roomIdsInBasket = (items: Item[]) =>
  items.map((item) => roomIds(item).roomId).filter((id): id is string => !!id);

export const toUpgradedRoomItem = (item: Item, upgradeRoomId: string) => {
  const { rateId, promoRateCode } = roomIds(item);
  const childAges = childAgesOf(item);
  const { note } = item as { note?: unknown };
  const guestDetails: (GuestDetail & { loggedInUser?: unknown })[] = Array.isArray(
    item.guestDetails
  )
    ? item.guestDetails
    : [];

  return {
    quantity: 1,
    type: 'rooms',
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    adults: item.adults ?? 0,
    children: item.children ?? 0,
    guestDetails: guestDetails.map(
      ({ title, firstName, lastName, gender, externalSpaId, loggedInUser }) => ({
        title,
        firstName,
        lastName,
        gender,
        externalSpaId,
        loggedInUser,
      })
    ),
    ...(typeof note === 'string' && note ? { note } : {}),
    bookable: {
      ids: { rateId, roomId: upgradeRoomId, promoRateCode: promoRateCode ?? null },
      ...(childAges.length ? { roomsChildAges: childAges } : {}),
    },
  };
};
